import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Download, X, ZoomIn, ZoomOut, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { logDev } from "@/lib/log";
import { trackContent } from "@/lib/track";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function useWidth(ref) {
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

function useReadingProgress(doc, pages, boxRef, onProgress, jumped) {
  const [current, setCurrent] = useState(doc.startPage || 1);
  const saved = useRef(doc.startPage || 1);
  useEffect(() => {
    if (!pages || !boxRef.current) return undefined;
    const io = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (vis) setCurrent(Number(vis.target.dataset.page));
    }, { root: boxRef.current, threshold: [0.25, 0.5, 0.75] });
    const t = setTimeout(() => boxRef.current?.querySelectorAll("[data-page]").forEach((el) => io.observe(el)), 300);
    return () => { clearTimeout(t); io.disconnect(); };
  }, [pages, boxRef]);
  useEffect(() => {
    if (!pages || !doc.id || !jumped.current || current === saved.current) return undefined;
    const t = setTimeout(() => {
      saved.current = current;
      api.put(`/ebooks/${doc.id}/progress`, { page: current, pages }).catch(logDev);
      onProgress && onProgress(doc.id, current, pages);
    }, 700);
    return () => clearTimeout(t);
  }, [current, pages, doc.id, onProgress, jumped]);
  return current;
}

export default function PdfViewerDialog({ doc, onClose, onProgress }) {
  const boxRef = useRef(null);
  const width = useWidth(boxRef);
  const [pages, setPages] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [failed, setFailed] = useState(false);
  const jumped = useRef(false);
  const current = useReadingProgress(doc, pages, boxRef, onProgress, jumped);
  const start = Math.min(doc.startPage || 1, pages || 1);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const onPageRendered = (n) => {
    if (jumped.current || n !== start) return;
    jumped.current = true;
    if (start > 1) boxRef.current?.querySelector(`[data-page="${start}"]`)?.scrollIntoView({ block: "start" });
  };

  const btn = "h-9 w-9 shrink-0 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white";
  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-slate-900" data-testid="pdf-viewer">
      <div className="flex items-center gap-2 px-3 sm:px-5 py-3 bg-slate-950 text-white">
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold truncate" data-testid="pdf-viewer-title">{doc.title}</div>
          <div className="text-[11px] font-mono-plex text-slate-400" data-testid="pdf-viewer-page">
            {pages ? `Page ${current} / ${pages}` : "Loading…"}
          </div>
        </div>
        <button className={btn} data-testid="pdf-zoom-out" onClick={() => setZoom((z) => Math.max(0.6, z - 0.2))} aria-label="Zoom out"><ZoomOut size={16} /></button>
        <button className={btn} data-testid="pdf-zoom-in" onClick={() => setZoom((z) => Math.min(3, z + 0.2))} aria-label="Zoom in"><ZoomIn size={16} /></button>
        <a href={doc.url} download={doc.filename} data-testid="pdf-download-btn" onClick={() => doc.id && trackContent("ebook", doc.id, "download")}
           className="h-9 px-3 sm:px-4 shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#00b4ff] hover:bg-[#0090cc] text-white text-sm font-semibold"
           style={{ transition: "background-color 0.2s ease" }}>
          <Download size={15} /> <span className="hidden sm:inline">Download</span>
        </a>
        <button className={btn} data-testid="pdf-close-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
      </div>
      {pages > 0 && <div className="h-1 bg-white/10"><div className="h-full bg-[#00b4ff]" style={{ width: `${(current / pages) * 100}%`, transition: "width 0.3s ease" }} /></div>}
      <div className="flex-1 overflow-auto" ref={boxRef} data-testid="pdf-viewer-scroll">
        {failed ? (
          <div className="p-10 text-center text-slate-300 text-sm" data-testid="pdf-viewer-error">
            Tak dapat paparkan PDF ini. Tekan Download untuk buka dalam phone anda.
          </div>
        ) : (
          <Document file={doc.url} onLoadSuccess={({ numPages }) => setPages(numPages)} onLoadError={() => setFailed(true)}
                    loading={<div className="p-10 grid place-items-center text-slate-300"><Loader2 className="animate-spin" /></div>}
                    className="flex flex-col items-start w-max min-w-full gap-3 py-3 px-2">
            {width > 0 && Array.from({ length: pages }, (_, i) => (
              <div key={i} data-page={i + 1} className="mx-auto" data-testid={`pdf-page-${i + 1}`}>
                <Page pageNumber={i + 1} width={Math.min(width - 16, 900) * zoom} className="shadow-lg bg-white"
                      onRenderSuccess={() => onPageRendered(i + 1)} />
              </div>
            ))}
          </Document>
        )}
      </div>
    </div>
  );
}
